import React, { useState } from 'react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  configApiRef,
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Button, Typography } from '@material-ui/core';

type Status = 'idle' | 'working' | 'done' | 'error';

export function AzureTechDocsCard() {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const generationEnabled =
    configApi.getOptionalBoolean('app.artaFeatures.techDocsGeneration') ?? true;

  const generate = async (all: boolean) => {
    setStatus('working');
    setMessage(all ? 'Migrating TechDocs for all Azure entities…' : 'Generating TechDocs…');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token = '' } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/techdocs/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(all ? {} : { entityRef: stringifyEntityRef(entity) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? res.statusText);
      setStatus(data.failed > 0 ? 'error' : 'done');
      setMessage(
        all
          ? `Updated ${data.succeeded} of ${data.total} entities${data.failed ? `; ${data.failed} failed` : ''}.`
          : data.results?.[0]?.changed
            ? 'TechDocs files were generated in the entity repository.'
            : 'TechDocs files are already current.',
      );
      if (data.failed === 0) {
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error: any) {
      setStatus('error');
      setMessage(error.message ?? 'TechDocs generation failed');
    }
  };

  if (!generationEnabled) return null;

  return (
    <InfoCard title="Generated TechDocs">
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Generate catalog-driven documentation and Mermaid architecture diagrams.
        Team-owned content in <code>docs/custom.md</code> is preserved.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => generate(false)}
        disabled={status === 'working'}
        style={{ marginRight: 8 }}
      >
        Refresh This Entity
      </Button>
      <Button
        variant="outlined"
        onClick={() => generate(true)}
        disabled={status === 'working'}
      >
        Migrate All Azure Entities
      </Button>
      {status === 'working' && <Progress />}
      {message && status !== 'working' && (
        <Typography
          variant="body2"
          color={status === 'error' ? 'error' : 'primary'}
          style={{ marginTop: 12 }}
        >
          {message}
        </Typography>
      )}
    </InfoCard>
  );
}
